import { addOutInv, orderFetch } from "@/apis/packing";
import { orderFetch as fetchOutOrder } from "@/apis/inventoryv2";
import { BoxStatus, PackBox, PackOrder, PackOrderAdd, PackOrderStatus } from "@/types/packing";
import { Button, Dropdown, Flex, Form, Input, Modal, notification, Space, Spin, Tag, Typography } from "antd";
import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BoxListSelectRef, PackOrderContext } from "./context";
import usePackOrderDetail from "./hooks";
import usePackEnums from "../../hooks/usePackEnums";
import SText from "@/styles/SText";
import { SConModal } from "@/styles/SConModal";
import PackBoxHome from "./PackBoxHome";
import AddLogistics from "@/pages/Shipment/pages/components/AddLogistics";
import LogisticsDetail from "@/pages/Shipment/pages/components/LogisticsDetail";
import SealOrder from "./PackBoxHome/SealOrder";
import OutOrdersList from "./OutOrdersList";
import AddOrderEdit from "@/pages/Shipment/pages/ShipmentListPage/components/AddOrderEdit";
import { Logistics, Shipment } from "@/types/shipment";
import { useMainCurrency } from "@/layout/hooks/useMainCurrency";
import { DictContext } from "@/layout/context/DictContext";
import { ItemType } from "antd/es/menu/interface";
import PrintPackOrder from "../PrintPackOrder";
import PackingExportButton from "../PackingExportButton";
import SelectOrderContent, { TabEnum } from "@/components/Selector/SelectOrderContent";
import { OrderItemV2, OrderV2 } from "@/types/inventoryV2";
import { ConfirmedEnum } from "@/types/inventory";
import { ExclamationCircleTwoTone } from "@ant-design/icons";
import { PurchaseOrderItem } from "@/types/purchase";

interface PackDetailProps {
  detail: PackOrder;
  open: boolean;
  onConfirm: (data: PackOrder) => void;
  onClose: () => void;
  readonly?: boolean;
};

const PackDetail: FC<PackDetailProps> = ({ detail, open, onConfirm, onClose, readonly = false }) => {
  const { t } = useTranslation();
  const { loading, orderData, sourceItems, sourceInventory,
    logistcsOrders, shipments, boxShipStatus, saleItems, inventoryOrders,
    setOrderData, setSourceItems, fetchData, doOrder, editItem,
    updateOrder, setBoxShipStatus, addShipment, addLogistics, setInventoryOrders
  } = usePackOrderDetail(detail);
  const { getPackStatus } = usePackEnums();
  const { exchangeRate, currencys, shipMethods, shipStatus } = useContext(DictContext);
  const { getMainCurrency } = useMainCurrency();
  const boxListRef = useRef<BoxListSelectRef>(null);

  const [desc, setDesc] = useState<string>('');
  const [openSealOrder, setOpenSealOrder] = useState(false);
  const [openOrderList, setOpenOrderList] = useState(false);
  const [openAddInv, setOpenAddInv] = useState(false);

  const [openAddShipBoxes, setOpenAddShipBoxes] = useState<PackBox[] | null>(null);
  const [mainCurrency, setMainCurrency] = useState<string>('USD');

  const [openLogis, setOpenLogis] = useState<Logistics | null>(null);
  const [openAddLogis, setOpenAddLogis] = useState<Shipment | null>(null);

  const orderLabe = getPackStatus(orderData?.status ?? null);

  const [outOrders, setOutOrders] = useState<OrderV2[]>([]);
  const [addOuting, setAddOuting] = useState<boolean>(false);
  const [sealStright, setSealStright] = useState<boolean>(false);
  const [sealStrighting, setSealStrighting] = useState<boolean>(false);

  useEffect(() => {
    if (open) {
      fetchData()
        .then(order => {
          setDesc(order?.remark ?? "");
        });
      getMainCurrency('', exchangeRate)
        .then(mainCur => {
          if (mainCur) {
            let main = currencys.find(cur => cur.key === mainCur.key);
            main && setMainCurrency(main.desc);
          }
        })
    }
  }, [open, exchangeRate, currencys]);

  const purLotMap = useMemo(() => {
    let resMap: Record<string, PurchaseOrderItem> = {};
    if (inventoryOrders?.length) {
      let orderItems: OrderItemV2[] = [];
      for (let ooi = 0; ooi < inventoryOrders.length; ooi++) {
        if (inventoryOrders[ooi].items?.length) orderItems.push(...inventoryOrders[ooi].items);
      };
      orderItems.forEach(oi => {
        let key = `${oi.productId}<->${oi.lot}`;
        if (!!!resMap[key] && oi.lotItem?.purchaseItem) resMap[key] = oi.lotItem.purchaseItem;
      });
    };
    return resMap;
  }, [inventoryOrders]);

  const boxShipStatusMap = useMemo(() => {
    let resMap: Record<string, boolean> = {};
    if (boxShipStatus.length > 0) {
      boxShipStatus.forEach(bs => {
        resMap[bs.packingBoxId] = !!bs.shipmentOrderId;
      })
    };
    return { ...resMap };
  }, [boxShipStatus]);

  const selOutIds = useMemo(() => {
    return outOrders.map(o => o.id)
  }, [outOrders]);

  const genShip = () => {
    let shipBoxes = boxListRef.current?.getSelectBox() || [];
    if (shipBoxes.length === 0) return notification.error({ message: t('warehouse.plSelBox') });
    else {
      let okBoxs: PackBox[] = [];
      let disBoxs: PackBox[] = [];
      shipBoxes.forEach(record => {
        if (boxShipStatusMap[record.id] === true || !!!record?.items?.length || record.status !== BoxStatus.SHUTED) {
          disBoxs.push(record);
        } else okBoxs.push(record);
      });
      if (!!disBoxs.length) {
        notification.warning({
          message: t('warehouse.selBoxErrTip1'),
          description: (
            <>
              <div>{t('warehouse.selBoxErrTip3')}：</div>
              <div style={{ color: "red" }}>{disBoxs.map(b => b.boxNumber).join(', ')}</div>
            </>
          )
        })
      }
      if (!!okBoxs.length) setOpenAddShipBoxes([...okBoxs]);
    }

  };

  const handleSuccess = (shipment: Shipment) => {
    let boxIds = openAddShipBoxes!.map(b => b.id);
    setBoxShipStatus(last => last.map(ss => ({
      ...ss,
      shipped: boxIds.includes(ss.packingBoxId),
      shipmentOrderId: boxIds.includes(ss.packingBoxId) ? shipment.id : null
    })));
    setOpenAddShipBoxes(null);
    boxListRef?.current?.clearSelect();
    addShipment(shipment);
  };

  const handleAddLogis = (logis: Logistics) => {
    addLogistics(logis);
    setOpenAddLogis(null);
  };

  const handleSaveDesc = () => {
    let info: PackOrderAdd = {
      orgId: orderData?.orgId ?? '',
      sourceOrderId: orderData?.sourceOrderId ?? '',
      shipmentOrderId: orderData?.shipmentOrderId ?? '',
      sourceOrderNumber: orderData?.sourceOrderNumber ?? '',
      remark: desc,
      inventoryOrderIds: null
    };
    updateOrder(info);
  };

  const getOpenLogisItems = useCallback(() => {
    let resItems: ItemType[] = [];
    logistcsOrders.forEach(logOrder => {
      let info = shipMethods.find(ship => ship.key === logOrder.shipMethod)?.desc
      resItems.push({
        key: logOrder.id,
        label: (
          <Flex gap={8} align='baseline' onClick={() => setOpenLogis(logOrder)}>
            <SText text={logOrder.trackingNo} style={{ maxWidth: 200 }} />
            {info && <Tag>{info}</Tag>}
          </Flex>
        )
      })
    });
    return resItems
  }, [logistcsOrders, shipMethods, setOpenLogis]);

  const getAddLogisItems = useCallback(() => {
    let resItems: ItemType[] = [];
    shipments.forEach(shipment => {
      let info = shipStatus.find(ship => ship.key === shipment.status)?.desc
      resItems.push({
        key: shipment.id,
        label: (
          <Flex gap={8} align='baseline' onClick={() => setOpenAddLogis(shipment)}>
            <SText text={shipment.number} style={{ maxWidth: 200 }} />
            {info && <Tag>{info}</Tag>}
          </Flex>
        )
      })
    });
    return resItems
  }, [shipments, shipStatus, setOpenLogis]);

  const handleAddInv = () => {
    if (orderData && selOutIds.length > 0) {
      setAddOuting(true);
      addOutInv(orderData.id, selOutIds)
        .then(async () => {
          notification.success({ message: t('common.addSuccess') });
          let newIds = outOrders.map(oo => oo.id);
          let resOrders = [...inventoryOrders];
          for (let i = 0; i < newIds.length; i++) {
            let newOrder = await fetchOutOrder(newIds[i]);
            resOrders.push(newOrder);
          }
          setInventoryOrders(resOrders);
          setOutOrders([]);
          setOpenAddInv(false);
        })
        .catch(err => {
          notification.error({ message: t('common.addError') })
        })
        .finally(() => setAddOuting(false))
    }
  };

  const handleConfirm = async () => {
    let empty = orderData?.boxes?.find(b => !!!b?.items?.length);
    if (!!empty) return notification.error({ message: t('warehouse.emptyBox', { number: empty.boxNumber }) });
    let notSeal = orderData?.boxes?.find(b => b.status === BoxStatus.DRAFT);
    if (!!!notSeal) setSealStright(true);
    else setOpenSealOrder(true);
  };

  const handleConfirmSealAll = async () => {
    setSealStrighting(true);
    try {
      await doOrder("confirm", undefined, true);
      await doOrder("seal-all", undefined);
      setSealStright(false);
    } catch (error) {
      setSealStright(false);
    }
  };

  const PackOrderTitle = (
    <Flex style={{ width: "100%" }} justify='space-between' >
      <Flex align='baseline' gap={8} >
        <Typography.Title level={5} style={{ margin: 0 }}>
          {t('warehouse.packingNo')}: {detail.number}
        </Typography.Title>
        {orderLabe && <SText tagMode color={orderLabe.color} text={orderLabe.label} tagStyle={{ width: 160 }} />}
        {inventoryOrders.length && <Button type='dashed' onClick={() => setOpenOrderList(true)}>{t('common.oo')}</Button>}
        {/* {false && orderData && <PrintPackOrder packOrder={orderData} disabled={loading === "PackOrder"} />} */}
      </Flex>
      <Flex align='center' gap={8} style={{ marginRight: 44 }}>
        {orderData && <PackingExportButton order={orderData} disabled={loading === "PackOrder"} />}
        {/* {orderData?.status === PackOrderStatus.SHUTED && <Button type='primary' disabled={readonly} onClick={genShip}>{t('warehouse.genShip')}</Button>} */}
        <Button type='primary' disabled={readonly} onClick={genShip}>{t('warehouse.genShip')}</Button>
        {orderData?.status === PackOrderStatus.DRAFT && <Button type='primary' disabled={readonly || !!(loading && ["PackOrderConfirm", "PackOrderSeal"].includes(loading))} onClick={handleConfirm}>{t('scan.confirm')}</Button>}
        {orderData?.status === PackOrderStatus.CONFIRMED && <Button type='dashed' disabled={readonly || loading === "PackOrderSeal"} onClick={() => doOrder('seal-all')} >{t('warehouse.closeBox')}</Button>}
        {orderData?.status === PackOrderStatus.DRAFT && (
          <SConModal disable={readonly || loading === "PackOrderDel"} onOk={async () => await doOrder('void')} title={t('common.tip')} content={t('warehouse.delTip', { no: detail.number })}>
            <Button type='text' danger disabled={readonly || loading === "PackOrderDel"} >{t('warehouse.killed')}</Button>
          </SConModal>
        )}
        {
          !!shipments.length && !!!loading && (
            <Dropdown menu={{ items: getAddLogisItems() }} trigger={["click"]} placement="bottomLeft" >
              <Button onClick={(e) => e.preventDefault()} type='link' disabled={readonly}>
                {t('shipment.addLogistics')}
              </Button>
            </Dropdown>
          )
        }
        {!!logistcsOrders.length && !!!loading && (
          <Dropdown menu={{ items: getOpenLogisItems() }} trigger={["click"]} placement="bottomLeft" >
            <Button onClick={(e) => e.preventDefault()}>
              {t('shipment.viewLogistics')}
            </Button>
          </Dropdown>
        )}
      </Flex>
    </Flex >
  );

  return (
    <Modal title={PackOrderTitle} width={1200} maskClosable={false}
      okButtonProps={{ disabled: loading === "PackOrder" }} open={open}
      onCancel={onClose} footer={null}
    >
      <Spin spinning={loading === "PackOrder"}>
        <Flex vertical style={{ height: "70vh" }} >
          {orderData && <PackOrderContext.Provider value={{ orderData, sourceItems, saleItems, setOrderData, setSourceItems, setBoxShipStatus, sourceInventory, boxListRef, boxShipStatus, mainCurrency, purLotMap }}>
            <Flex gap={8} align='center' >
              <Form.Item label={t('order.remark')} style={{ flex: 1 }}>
                <Input.TextArea value={desc}
                  onChange={e => setDesc(e.target.value)}
                  autoSize={{ minRows: 2, maxRows: 2 }} maxLength={1000} showCount
                  readOnly={orderData.status !== PackOrderStatus.DRAFT}
                />
              </Form.Item>
              {orderData.status === PackOrderStatus.DRAFT && <Button type='primary' onClick={handleSaveDesc} disabled={readonly || loading === "PackOrderUpdate" || desc === orderData.remark}>{t('common.save')}</Button>}
            </Flex>
            <PackBoxHome readonly={readonly} editItem={editItem} />
            <SealOrder
              open={openSealOrder}
              onClose={() => setOpenSealOrder(false)}
            />
          </PackOrderContext.Provider>}
        </Flex>
      </Spin>
      {openAddLogis && <AddLogistics
        open
        pack={null}
        shipment={openAddLogis}
        onClose={() => setOpenAddLogis(null)}
        onSuccess={handleAddLogis}
      />}
      {openLogis && <LogisticsDetail
        logistics={openLogis}
        open
        readonly={readonly}
        onClose={() => setOpenLogis(null)}
        onSuccess={() => setOpenLogis(null)}
      />}
      <OutOrdersList
        number={orderData?.number ?? ''}
        open={openOrderList}
        list={inventoryOrders ?? []}
        onClose={() => setOpenOrderList(false)}
        btns={
          orderData?.status === PackOrderStatus.DRAFT && <Button
            onClick={() => setOpenAddInv(true)}
            disabled={readonly}
            loading={addOuting}
          >{t('warehouse.addOutOrder')}</Button>
        }
      />
      {
        openAddShipBoxes && <AddOrderEdit
          formType="ADD" open
          onClose={() => setOpenAddShipBoxes(null)}
          onSuccess={handleSuccess}
          shipment={null}
          packingOrderId={orderData!.id}
          shipBoxes={openAddShipBoxes}
          saleItems={saleItems}
          purLotMap={purLotMap}
        />
      }
      {
        openAddInv && (
          <Modal title={t('warehouse.addOutOrder')} width={1000} centered maskClosable={false}
            onCancel={() => setOpenAddInv(false)} onOk={handleAddInv} open okButtonProps={{ loading: addOuting }}>
            <Flex vertical style={{ height: "60vh" }}>
              <SelectOrderContent
                selectedIds={selOutIds}
                disIds={inventoryOrders?.map(inv => inv.id) ?? []}
                selectType="checkbox"
                onSelectOrder={setOutOrders}
                // initOrders={inventories}
                onlyTab={TabEnum.OUTBOUND}
                defaultConfirm={ConfirmedEnum.CONFIRM}
              />
            </Flex>
          </Modal>
        )
      }
      <Modal title={
        <Space>
          <ExclamationCircleTwoTone />
          <span>{t('common.tip')}</span>
        </Space>
      } open={sealStright} onOk={handleConfirmSealAll} onCancel={() => setSealStright(false)}
        okButtonProps={{ loading: sealStrighting }}
      >
        {t('warehouse.sealAllTip')}
      </Modal>
    </Modal>
  )
};

export default PackDetail;
